import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { formatCurrency, formatDate, getStatusColor } from '../lib/format';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Users, Briefcase, FileText, DollarSign, AlertTriangle, TrendingUp, Landmark, CreditCard } from 'lucide-react';

function formatAccountBalance(value, currency) {
  if (value === null || value === undefined) return 'Not available';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'CAD',
    }).format(Number(value));
  } catch (_) {
    return `${currency || 'CAD'} ${Number(value).toFixed(2)}`;
  }
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [bankingStatus, setBankingStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/banking/status').then(r => setBankingStatus(r.data)).catch(() => setBankingStatus(null));
    api.get('/dashboard').then(r => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Dashboard</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="border border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <div className="h-20 bg-slate-100 animate-pulse rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const kpis = [
    { label: 'Total Clients', value: data.total_clients, icon: Users, color: 'text-blue-600 bg-blue-50' },
    { label: 'Active Projects', value: data.active_projects, icon: Briefcase, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Open Invoices', value: data.open_invoices, icon: FileText, color: 'text-amber-600 bg-amber-50' },
    { label: 'Total Receivable', value: formatCurrency(data.total_receivable), icon: DollarSign, color: 'text-blue-600 bg-blue-50' },
    { label: 'Total Collected', value: formatCurrency(data.total_collected), icon: TrendingUp, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Open Expenses', value: formatCurrency(data.open_expenses || 0), icon: DollarSign, color: 'text-rose-600 bg-rose-50' },
    {
      label: 'Estimated Tax Payable',
      value: formatCurrency(data.estimated_tax_payable || 0),
      subtitle: 'Invoice tax less credit-note tax',
      icon: Landmark,
      color: 'text-violet-600 bg-violet-50',
    },
  ];
  const bankAccounts = (bankingStatus?.connections || [])
    .filter((connection) => connection.status !== 'disconnected')
    .flatMap((connection) => (connection.bank_accounts || []).map((account) => ({
      ...account,
      institution_name: connection.institution_name || 'Connected bank',
    })));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="dashboard-title">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Overview of your business performance</p>
      </div>

      {bankAccounts.length > 0 && (
        <section aria-labelledby="bank-balances-heading">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 id="bank-balances-heading" className="text-lg font-semibold text-slate-900">Bank balances / Kontostände</h2>
              <p className="text-xs text-slate-500">Swipe or scroll to view each connected account and credit card.</p>
            </div>
            <Link to="/banking" className="text-sm text-blue-600 hover:underline">Manage accounts</Link>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-3 snap-x snap-mandatory" data-testid="dashboard-bank-balances">
            {bankAccounts.map((account) => {
              const isCredit = account.account_type === 'credit';
              const AccountIcon = isCredit ? CreditCard : Landmark;
              return (
                <Card key={account.id} className="min-w-[270px] sm:min-w-[310px] snap-start border border-slate-200 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-slate-500 truncate">{account.institution_name}</p>
                        <p className="text-base font-semibold text-slate-900 truncate mt-1">{account.name}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {account.account_subtype || account.account_type || 'Account'}{account.mask ? ` ••••${account.mask}` : ''}
                        </p>
                      </div>
                      <div className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center ${isCredit ? 'text-violet-600 bg-violet-50' : 'text-blue-600 bg-blue-50'}`}>
                        <AccountIcon className="w-5 h-5" />
                      </div>
                    </div>
                    <div className="mt-5">
                      <p className="text-xs text-slate-500">{isCredit ? 'Card balance' : 'Current balance'}</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">{formatAccountBalance(account.current_balance, account.currency)}</p>
                      {!isCredit && account.available_balance !== null && account.available_balance !== undefined && (
                        <p className="text-xs text-slate-500 mt-2">
                          Available: {formatAccountBalance(account.available_balance, account.currency)}
                        </p>
                      )}
                      {!account.is_active && (
                        <p className="text-xs text-slate-400 mt-2">Excluded from payment matching</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {kpis.map((kpi) => (
          <Card key={kpi.label} className="border border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">{kpi.label}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{kpi.value}</p>
                  {kpi.subtitle && (
                    <p className="text-xs text-slate-400 mt-1">{kpi.subtitle}</p>
                  )}
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${kpi.color}`}>
                  <kpi.icon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Profit Snapshot */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-slate-500">Revenue</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(data.total_invoiced || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-slate-500">Expenses</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(data.total_expenses || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-slate-500">Estimated Profit</p>
            <p className={`text-2xl font-bold mt-1 ${(data.estimated_profit || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {formatCurrency(data.estimated_profit || 0)}
            </p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-slate-500">Estimated Margin</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{Number(data.estimated_margin_pct || 0).toFixed(1)}%</p>
          </CardContent>
        </Card>
      </div>

      {data.overdue_invoices > 0 && (
        <Card className="border border-rose-200 bg-rose-50 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-rose-600" />
            <p className="text-sm font-medium text-rose-700">
              You have {data.overdue_invoices} overdue invoice{data.overdue_invoices > 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Invoices */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-medium text-slate-800">Recent Invoices</CardTitle>
              <Link to="/invoices" className="text-sm text-blue-600 hover:underline">View all</Link>
            </div>
          </CardHeader>
          <CardContent>
            {data.recent_invoices.length === 0 ? (
              <p className="text-sm text-slate-500 py-4">No invoices yet</p>
            ) : (
              <div className="space-y-3">
                {data.recent_invoices.map((inv) => {
                  const dateLabel = formatDate(inv.invoice_date);
                  const contextLabel = inv.project_name || inv.client_name;
                  const subtitle = contextLabel ? `${dateLabel} · ${contextLabel}` : dateLabel;
                  return (
                  <div key={inv.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{inv.invoice_number}</p>
                      <p className="text-xs text-slate-500">{subtitle}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-slate-900">{formatCurrency(inv.total)}</p>
                      <Badge className={`text-xs ${getStatusColor(inv.status)}`}>{inv.status}</Badge>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Payments */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-medium text-slate-800">Recent Payments</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {data.recent_payments.length === 0 ? (
              <p className="text-sm text-slate-500 py-4">No payments yet</p>
            ) : (
              <div className="space-y-3">
                {data.recent_payments.map((pay) => (
                  <div key={pay.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{formatCurrency(pay.amount)}</p>
                      <p className="text-xs text-slate-500">{pay.payment_method.replace('_', ' ')}</p>
                    </div>
                    <p className="text-xs text-slate-500">{formatDate(pay.payment_date)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
